import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { CustomTheme } from '@masfront/common-b2c'
import { useFormatMessage } from '@masfront/common-i18n'

import { Button, TextField, CircularProgress, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import SearchOutlined from '@material-ui/icons/SearchOutlined'

import { getDocumentType, isValidNIF } from './inputDocumentType.helpers'
import useStylesText from './InputDocumentTypeStyles'

import { useBrand } from '~/brand'
import { isPhoneNumberType } from '~/common/helper'
import { DocumentInputType } from '~/common/model'
import { makePathname } from '~/net'
import { PATHS } from '~/pages/router/paths'

const useStyles = makeStyles((theme: CustomTheme) => {
  return {
    root: {
      width: '40%',
    },
    inputDocumentType: {
      borderRadius: '4px',
      margin: '0px',
      minHeight: '0px',
      '& .MuiInputBase-root:focus-within:not(.Mui-error)': {
        boxShadow: `0 0 2px 1px ${theme.palette.primary.dark}`,
      },
      '& .Mui-error': {
        border: `2px solid ${theme.palette.error.main}`,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
    textfieldAdornment: {
      marginRight: theme.spacing(6),
    },
    documentType: {
      marginRight: theme.spacing(3),
    },
    clearBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      '&:hover': {
        background: 'none',
      },
    },
  }
})

interface InputDocumentTypeProps {
  className?: string
  isLoading: boolean
  disabled?: boolean
}

enum InputStatus {
  ENTERING_DATA = 'ENTERING_DATA',
  READY = 'READY',
  ERROR = 'ERROR',
}

const InputDocumentType: React.FC<InputDocumentTypeProps> = ({
  className,
  isLoading,
  disabled,
}) => {
  const formatMessage = useFormatMessage()
  const history = useHistory()
  const { brand } = useBrand()
  const styles = useStyles()
  const stylesText = useStylesText()

  const [state, setState] = useState({
    modelInput: '',
    documentType: '',
  })

  const [inputStatus, setInputStatus] = useState(InputStatus.ENTERING_DATA)

  function getPaddedDocument(document: string) {
    const paddedValue = document.padStart(9, '0')
    return isValidNIF(paddedValue) ? paddedValue : document
  }

  function matchDocumentType(cleanVal: string) {
    const documentType = getDocumentType(cleanVal) || ''
    setState({ documentType, modelInput: cleanVal })
  }

  function updateModelInput(evt: React.ChangeEvent<HTMLInputElement>) {
    setState({
      modelInput: evt.target.value.trim().toUpperCase(),
      documentType: getDocumentType(evt.target.value.trim().toUpperCase()),
    })
  }

  function clearInput() {
    setState({ modelInput: '', documentType: '' })
  }

  function handleSearch() {
    matchDocumentType(state.modelInput)
    if (state.documentType) {
      setInputStatus(InputStatus.READY)

      if (isPhoneNumberType(state.documentType as DocumentInputType)) {
        history.push({
          pathname: makePathname(PATHS.PRODUCT, {
            customerIdentifier: getPaddedDocument(state.modelInput),
            productId: getPaddedDocument(state.modelInput),
          }),
          search: `?brand=${brand}`,
        })
      } else {
        history.push({
          pathname: makePathname(PATHS.CUSTOMER, {
            customerIdentifier: getPaddedDocument(state.modelInput),
          }),
          search: `?brand=${brand}`,
        })
      }
    } else {
      setInputStatus(InputStatus.ERROR)
    }
  }

  function handleKeyPress(key: string) {
    if (key === 'Enter') {
      handleSearch()
    } else {
      setInputStatus(InputStatus.ENTERING_DATA)
    }
  }

  useEffect(() => {
    clearInput()
  }, [brand])

  return (
    <div className={`${className}`}>
      <TextField
        disabled={disabled}
        name="search"
        type="text"
        className={`${styles.inputDocumentType} ${stylesText.root}`}
        variant="outlined"
        fullWidth
        error={inputStatus === InputStatus.ERROR}
        margin="dense"
        value={state.modelInput}
        placeholder={formatMessage('header_input_document_type_placeholder', 'missing!!')}
        onChange={updateModelInput}
        autoComplete="off"
        InputProps={{
          onKeyDown: (e) => handleKeyPress(e.key),
          startAdornment: isLoading ? (
            <CircularProgress size={25} className={styles.textfieldAdornment} />
          ) : (
            <SearchOutlined className={styles.textfieldAdornment} />
          ),
          endAdornment:
            state.documentType && inputStatus === InputStatus.READY ? (
              <Typography className={styles.documentType} data-hook="document-type">
                {state.documentType === 'PASSPORT' ? 'Pasaporte' : state.documentType}
              </Typography>
            ) : (
              <Button className={styles.clearBtn}>
                <CloseIcon onClick={() => clearInput()} />
              </Button>
            ),
        }}
        inputProps={{
          'data-hook': 'input-document-type',
        }}
      />
    </div>
  )
}

export default InputDocumentType
